import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import subHeadquarterService from '../../services/subHeadquarter.service';
import headquarterService from '../../services/headquarter.service';
import regionService from '../../services/region.service'; // Region service import karna mat bhulna
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const EditSubHeadquarter = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [headquarters, setHeadquarters] = useState([]);
  const [regions, setRegions]           = useState([]); // Regions list ke liye
  const [toast, setToast]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors]             = useState({});

  const [formData, setFormData] = useState({
    name:          '',
    regionId:      '', // Backend demand: regionId
    headquarterId: '', // Backend demand: headquarterId
  });

 useEffect(() => {
  const init = async () => {
    try {
      const companyId = isSuperAdmin
        ? localStorage.getItem('selectedCompanyId')
        : user?.companyId;

      // 1. Sabse pehle Sub-HQ details load karein (Yeh main hai)
      try {
        const subHqRes = await subHeadquarterService.getSubheadquarterById(id);
        if (subHqRes && subHqRes.success) {
          const s = subHqRes.data;
          setFormData({
            name:          s.name || '',
            regionId:      s.regionId ? String(s.regionId) : '',
            headquarterId: s.headquarterId ? String(s.headquarterId) : '',
          });
        } else {
          // Agar main data hi nahi mila toh toast dikhayein
          setToast({ message: 'Failed to load sub-headquarter data', type: 'error' });
        }
      } catch (subHqErr) {
        console.error("SubHQ API Error:", subHqErr);
        setToast({ message: 'Failed to load sub-headquarter data', type: 'error' });
      }

      // 2. Headquarters load karein (Alag try-catch mein taaki main page crash na ho)
      try {
        const hqListRes = await headquarterService.getHeadquarters(companyId, 1, 100);
        if (hqListRes && hqListRes.success && hqListRes.data) {
          const hqArray = Array.isArray(hqListRes.data) 
            ? hqListRes.data 
            : (hqListRes.data.headquarters || hqListRes.data.data || []);
          setHeadquarters(hqArray);
        }
      } catch (hqErr) {
        console.error("HQ List API Error:", hqErr);
      }

      // 3. Regions load karein (Alag try-catch mein)
      try {
        const regionsRes = await regionService.getAllRegions(companyId);
        if (regionsRes && regionsRes.success && regionsRes.data) {
          setRegions(regionsRes.data);
        }
      } catch (regionErr) {
        console.error("Regions API Error:", regionErr);
      }

    } catch (err) {
      console.error("General Init Error:", err);
    } finally {
      setLoading(false);
    }
  };
  init();
}, [id, isSuperAdmin, user?.companyId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = 'Sub-Headquarter name is required';
    if (!formData.headquarterId) e.headquarterId = 'Headquarter is required';
    // Region check optional hai front-end par, par backend ko payload mein chahiye
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      // Backend controller ke hisab se payload structure
      const payload = {
        name:          formData.name.trim(),
        regionId:      parseInt(formData.regionId, 10),
        headquarterId: parseInt(formData.headquarterId, 10),
      };

      const res = await subHeadquarterService.updateSubHeadquarter(id, payload);
      if (res.success) {
        setToast({ message: 'Sub-Headquarter updated successfully!', type: 'success' });
        setTimeout(() => navigate('/subheadquarter/view'), 1200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update sub-headquarter',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="add-headquarter-page">
        <p style={{ padding: 40 }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="add-headquarter-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/subheadquarter/view">Sub-Headquarters</a></li>
          <li className="breadcrumb-item active">Edit Sub-Headquarter</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Edit Sub-Headquarter</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <div className="form-row">
              {/* Sub-Headquarter Name Input */}
              <div className="form-group">
                <label className="form-label">
                  Sub-Headquarter Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter sub-headquarter name"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              {/* Headquarter Select Dropdown */}
              <div className="form-group">
                <label className="form-label">
                  Headquarter <span className="required">*</span>
                </label>
                <select
                  name="headquarterId"
                  className={`form-input ${errors.headquarterId ? 'error' : ''}`}
                  value={formData.headquarterId}
                  onChange={handleChange}
                >
                  <option value="">Select Headquarter</option>
                  {headquarters.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </select>
                {errors.headquarterId && <span className="error-message">{errors.headquarterId}</span>}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/subheadquarter/view')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSubHeadquarter;